"use client";

// 네이버 예약 스타일 달력 + 시간 슬롯 선택 (실시간 1:1 피드백용, 다크 테마)

import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, CalendarX } from "lucide-react";
import {
  buildSlotsByDate,
  formatDateKey,
  listAvailabilities,
  listUpcomingBookings,
  type SlotOption,
} from "@/lib/realtimeBooking/api";

interface SlotPickerProps {
  selectedSlot: SlotOption | null;
  onSelect: (slot: SlotOption | null) => void;
  /** 지정하면 해당 선생님의 가능 시간만 표시 */
  instructorId?: string;
}

const DAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

export default function SlotPicker({
  selectedSlot,
  onSelect,
  instructorId,
}: SlotPickerProps) {
  const [loading, setLoading] = useState(true);
  const [slotsByDate, setSlotsByDate] = useState<Map<string, SlotOption[]>>(
    new Map()
  );
  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const all = await listAvailabilities();
        const availabilities = instructorId
          ? all.filter(a => a.instructorId === instructorId)
          : all;
        const taken = await listUpcomingBookings(
          availabilities.map(a => a.instructorId)
        );
        if (cancelled) return;
        setSlotsByDate(buildSlotsByDate(availabilities, taken));
      } catch (error) {
        console.error("예약 가능 시간 조회 실패:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [instructorId]);

  // 달력 그리드 (이전달 잔여일 포함 6주)
  const calendarCells = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const startOffset = firstDay.getDay();
    const cells: { date: Date; inMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(viewYear, viewMonth, 1 - startOffset + i);
      cells.push({ date: d, inMonth: d.getMonth() === viewMonth });
    }
    // 마지막 주가 전부 다음달이면 제거
    while (
      cells.length > 35 &&
      cells.slice(35).every(c => !c.inMonth)
    ) {
      cells.splice(35);
    }
    return cells;
  }, [viewYear, viewMonth]);

  const todayKey = formatDateKey(today);
  const daySlots = selectedDate ? slotsByDate.get(selectedDate) || [] : [];
  const morningSlots = daySlots.filter(s => Number(s.time.split(":")[0]) < 12);
  const afternoonSlots = daySlots.filter(
    s => Number(s.time.split(":")[0]) >= 12
  );

  const goPrevMonth = () => {
    const d = new Date(viewYear, viewMonth - 1, 1);
    // 이번 달 이전으로는 이동 불가
    if (
      d.getFullYear() < today.getFullYear() ||
      (d.getFullYear() === today.getFullYear() &&
        d.getMonth() < today.getMonth())
    ) {
      return;
    }
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const goNextMonth = () => {
    const d = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const formatTimeLabel = (time: string) => {
    const [h, m] = time.split(":").map(Number);
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${String(m).padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        예약 가능한 시간을 불러오는 중...
      </div>
    );
  }

  if (slotsByDate.size === 0) {
    return (
      <div className="text-center py-8 text-gray-400 bg-gray-800/50 rounded-2xl">
        <CalendarX className="w-8 h-8 mx-auto mb-2 text-gray-500" />
        <p className="text-sm">
          현재 예약 가능한 시간이 없습니다.
          <br />
          신청 후 프로와 일정을 조율해 드릴게요.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 월 이동 헤더 */}
      <div className="flex items-center justify-center gap-6">
        <button
          type="button"
          onClick={goPrevMonth}
          className="p-1.5 text-gray-400 hover:text-white transition-colors"
          aria-label="이전 달"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-lg font-bold text-white">
          {viewYear}.{viewMonth + 1}
        </span>
        <button
          type="button"
          onClick={goNextMonth}
          className="p-1.5 text-gray-400 hover:text-white transition-colors"
          aria-label="다음 달"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 text-center">
        {DAY_LABELS.map((label, i) => (
          <span
            key={label}
            className={`text-xs font-medium py-1 ${
              i === 0
                ? "text-rose-400"
                : i === 6
                  ? "text-blue-400"
                  : "text-gray-400"
            }`}
          >
            {label}
          </span>
        ))}
      </div>

      {/* 날짜 그리드 */}
      <div className="grid grid-cols-7 gap-y-1">
        {calendarCells.map(({ date, inMonth }) => {
          const key = formatDateKey(date);
          const hasSlots = slotsByDate.has(key);
          const isSelected = selectedDate === key;
          const isToday = key === todayKey;

          return (
            <div key={key} className="flex flex-col items-center">
              <button
                type="button"
                disabled={!inMonth || !hasSlots}
                onClick={() => {
                  setSelectedDate(key);
                  onSelect(null);
                }}
                className={`w-10 h-10 rounded-xl text-sm font-medium transition-all ${
                  !inMonth
                    ? "text-gray-700 cursor-default"
                    : isSelected
                      ? "bg-green-500 text-white ring-2 ring-green-400 font-bold"
                      : hasSlots
                        ? "text-green-400 hover:bg-green-500/20"
                        : "text-gray-600 cursor-default"
                }`}
              >
                {date.getDate()}
              </button>
              {inMonth && isToday && (
                <span className="text-[10px] text-green-400 -mt-0.5">오늘</span>
              )}
            </div>
          );
        })}
      </div>

      {/* 시간 슬롯 */}
      {selectedDate && (
        <div className="space-y-4 pt-2 border-t border-gray-700/60">
          {daySlots.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              선택한 날짜에 예약 가능한 시간이 없습니다.
            </p>
          )}

          {morningSlots.length > 0 && (
            <div>
              <p className="text-sm text-gray-400 mb-2">오전</p>
              <div className="grid grid-cols-4 gap-2">
                {morningSlots.map(slot => {
                  const isActive =
                    selectedSlot?.date === slot.date &&
                    selectedSlot?.time === slot.time;
                  return (
                    <button
                      key={slot.time}
                      type="button"
                      onClick={() => onSelect(isActive ? null : slot)}
                      className={`py-2.5 rounded-xl text-sm font-medium border transition-all ${
                        isActive
                          ? "bg-green-500 border-green-400 text-white"
                          : "border-gray-600 text-gray-200 hover:border-green-500/60"
                      }`}
                    >
                      {formatTimeLabel(slot.time)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {afternoonSlots.length > 0 && (
            <div>
              <p className="text-sm text-gray-400 mb-2">오후</p>
              <div className="grid grid-cols-4 gap-2">
                {afternoonSlots.map(slot => {
                  const isActive =
                    selectedSlot?.date === slot.date &&
                    selectedSlot?.time === slot.time;
                  return (
                    <button
                      key={slot.time}
                      type="button"
                      onClick={() => onSelect(isActive ? null : slot)}
                      className={`py-2.5 rounded-xl text-sm font-medium border transition-all ${
                        isActive
                          ? "bg-green-500 border-green-400 text-white"
                          : "border-gray-600 text-gray-200 hover:border-green-500/60"
                      }`}
                    >
                      {formatTimeLabel(slot.time)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
